/**
 * EntityDiagramsTab — reusable Diagrams tab embedded into
 * ClientDetail / BrandDetail / ProjectDetail.
 *
 * Props: entityType ("client"|"brand"|"project"), entityId, entityName
 *
 * Behaviour:
 *   • Fetches diagrams filtered by the entity id via `client_id` /
 *     `brand_id` / `project_id` (backend list_diagrams).
 *   • Provides an inline "New diagram" button that creates a diagram
 *     scoped to that entity and jumps to the editor.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GitFork, Plus, ExternalLink, Loader2 } from "lucide-react";

export function EntityDiagramsTab({ entityType, entityId, entityName }) {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (!activeWorkspaceId || !entityId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = { [`${entityType}_id`]: entityId };
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/diagrams`, { params });
        if (!cancel) setItems(data.items || []);
      } catch (e) {
        if (!cancel) toast.error(apiErrorMessage(e, "Failed to load diagrams"));
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [activeWorkspaceId, entityType, entityId]);

  const create = async () => {
    if (!title.trim()) return toast.error("Enter a title");
    setCreating(true);
    try {
      const body = {
        title: title.trim(),
        scope: entityType,
        [`${entityType}_id`]: entityId,
      };
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/diagrams`, body);
      nav(`/app/diagrams/${data.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Create failed"));
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-3" data-testid={`${entityType}-diagrams-tab`}>
      <div className="flex items-end gap-2 border p-3 bg-neutral-50">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">New diagram for {entityName}</div>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Onboarding process · Weekly review cadence"
            className="h-9 rounded-none mt-1"
            data-testid="entity-diagrams-title"
          />
        </div>
        <Button className="rounded-none h-9" onClick={create} disabled={creating || !title.trim()} data-testid="entity-diagrams-create">
          {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading diagrams…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground border p-6 text-center">
          <GitFork className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
          No diagrams yet for this {entityType}. Create one above to map processes, escalation paths, or workflows.
        </div>
      ) : (
        <div className="border">
          <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-muted-foreground border-b p-2 bg-neutral-50">
            <div className="col-span-6">Title</div>
            <div className="col-span-2">Scope</div>
            <div className="col-span-2 text-right">Nodes · Edges</div>
            <div className="col-span-2 text-right">Updated</div>
          </div>
          {items.map((d) => (
            <button
              key={d.id}
              onClick={() => nav(`/app/diagrams/${d.id}`)}
              className="w-full text-left grid grid-cols-12 items-center p-2 text-xs border-b hover:bg-neutral-50 transition-colors"
              data-testid={`entity-diagram-row-${d.id}`}
            >
              <div className="col-span-6 flex items-center gap-2">
                <GitFork className="w-3.5 h-3.5 text-muted-foreground" />
                <div>
                  <div className="font-medium">{d.title}</div>
                  {d.description && <div className="text-[10px] text-muted-foreground truncate">{d.description}</div>}
                </div>
              </div>
              <div className="col-span-2 text-[10px] uppercase text-muted-foreground">{d.scope}</div>
              <div className="col-span-2 text-right text-[10px] text-muted-foreground">{d.node_count} · {d.edge_count}</div>
              <div className="col-span-2 text-right text-[10px] text-muted-foreground flex items-center justify-end gap-1">
                {(d.updated_at || "").slice(0, 10)}
                <ExternalLink className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
